import {
  EventType,
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
  type EventMessage,
  type IPublicClientApplication,
} from '@azure/msal-browser'
import type { AppConfig } from './config'

export const STUB_TOKEN = 'stub-token'

export class AuthError extends Error {
  readonly code: 'no-account' | 'interaction-required' | 'unknown'
  constructor(message: string, code: 'no-account' | 'interaction-required' | 'unknown') {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

export type AuthClient = {
  getActiveAccount(): AccountInfo | null
  acquireToken(): Promise<string>
  loginRedirect(): Promise<void>
  /** Forget the signed-in account locally without ending the Microsoft SSO
   * session — the next login is a one-click account pick, like first load. */
  logoutLocal(): Promise<void>
}

export function buildMsalConfig(config: Extract<AppConfig['auth'], { kind: 'entra' }>): Configuration {
  return {
    auth: {
      clientId: config.spaClientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  }
}

export function createStubAuthClient(): AuthClient {
  return {
    getActiveAccount: () => ({
      homeAccountId: 'stub',
      environment: 'stub',
      tenantId: 'stub',
      username: 'stub@local',
      localAccountId: '00000000-0000-0000-0000-000000000000',
    }),
    acquireToken: () => Promise.resolve(STUB_TOKEN),
    loginRedirect: () => Promise.resolve(),
    logoutLocal: () => Promise.resolve(),
  }
}

export function createEntraAuthClient(
  pca: IPublicClientApplication,
  apiScope: string,
): AuthClient {
  const scopes = [apiScope]
  return {
    getActiveAccount: () => pca.getActiveAccount(),
    acquireToken: async () => {
      const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0]
      if (!account) {
        throw new AuthError('No active account; sign in first.', 'no-account')
      }
      try {
        const result = await pca.acquireTokenSilent({ scopes, account })
        return result.accessToken
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          const result = await pca.acquireTokenPopup({ scopes, account })
          return result.accessToken
        }
        throw new AuthError(
          err instanceof Error ? err.message : 'Token acquisition failed',
          'unknown',
        )
      }
    },
    loginRedirect: () => pca.loginRedirect({ scopes }),
    // Local-only logout: returning false from onRedirectNavigate makes MSAL
    // clear its account/token cache but skip the trip to the end-session
    // endpoint, so the Microsoft SSO cookie survives for quick re-login.
    logoutLocal: () => pca.logoutRedirect({ onRedirectNavigate: () => false }),
  }
}

export async function buildPublicClientApplication(
  config: Extract<AppConfig['auth'], { kind: 'entra' }>,
): Promise<PublicClientApplication> {
  const pca = new PublicClientApplication(buildMsalConfig(config))

  // Register the event callback BEFORE initialize/handleRedirectPromise so the
  // LOGIN_SUCCESS that handleRedirectPromise fires lands in this callback —
  // which is what makes msal-react's accounts state stay in sync. Without
  // this, the redirect-back lands on the unauth branch until a page reload.
  pca.addEventCallback((event: EventMessage) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload &&
      'account' in event.payload &&
      event.payload.account
    ) {
      pca.setActiveAccount(event.payload.account as AccountInfo)
    }
  })

  await pca.initialize()
  await pca.handleRedirectPromise()

  // Returning user with a cached session (no redirect this load): pick up the
  // first cached account so subsequent acquireTokenSilent calls succeed.
  if (!pca.getActiveAccount()) {
    const accounts = pca.getAllAccounts()
    if (accounts[0]) {
      pca.setActiveAccount(accounts[0])
    }
  }

  return pca
}
